import { UtilS } from '@modules/Util/S/Function/module/UtilS.js';
import { UtilLoggerS } from '@modules/Util/Logger/S/Function/module/UtilLoggerS';

type LoggerColor = 'yellow' | 'orange' | 'green' | 'blue' | 'red' | 'cyan' | 'magenta' | 'white';

interface ColorMapping {
  bg: string;
  fg: string;
}

export class UtilLogger {
  // eslint-disable-next-line no-use-before-define
  private static instance: UtilLogger;

  private readonly colorMap: Record<LoggerColor, ColorMapping> = {
    yellow: { bg: 'bg_yellow', fg: 'black' },
    orange: { bg: 'bg_red', fg: 'black' }, // ANSI에 orange 없음, red로 대체
    green: { bg: 'bg_green', fg: 'black' },
    blue: { bg: 'bg_blue', fg: 'white' },
    red: { bg: 'bg_red', fg: 'white' },
    cyan: { bg: 'bg_cyan', fg: 'black' },
    magenta: { bg: 'bg_magenta', fg: 'white' },
    white: { bg: 'bg_white', fg: 'black' },
  };

  private readonly defaultColor: LoggerColor = 'blue';

  private constructor() {}

  /**
   * Get singleton instance of UtilLogger
   * @returns UtilLogger instance
   */
  static getInstance(): UtilLogger {
    if (!UtilLogger.instance) {
      UtilLogger.instance = new UtilLogger();
    }
    return UtilLogger.instance;
  }

  /**
   * Get colors for logger display
   * @param color - Color name for logger display
   * @returns Color mapping object
   */
  private getColors(color?: LoggerColor): ColorMapping {
    return this.colorMap[color || this.defaultColor];
  }

  /**
   * Check if running in production environment or test mode
   * @returns True if production environment or Jest test mode
   */
  private isProductionOrTest(): boolean {
    try {
      // Check if in Jest test environment
      if (
        typeof jest !== 'undefined' ||
        (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') ||
        (typeof process !== 'undefined' && process.env?.JEST_WORKER_ID !== undefined)
      ) {
        return true;
      }

      // Check production environment
      return UtilS.isProd();
    } catch {
      // Fallback for environments where UtilS is not available
      if (typeof process !== 'undefined' && process.env) {
        return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test';
      }
      return false;
    }
  }

  /**
   * Log a message with colored background
   * @param message - Message to log
   * @param color - Color for log display
   */
  log(message: string, color?: LoggerColor): void {
    if (this.isProductionOrTest()) return;

    const { bg, fg } = this.getColors(color);

    try {
      UtilLoggerS.colorLog([` ${message} `], [fg], [bg]);
    } catch {
      // Fallback for environments where UtilLoggerS is not available
      console.log(`[LOG] ${message}`);
    }
  }

  /**
   * Log an info message with blue color
   * @param message - Message to log
   */
  info(message: string): void {
    this.log(message, 'blue');
  }

  /**
   * Log a success message with green color
   * @param message - Message to log
   */
  success(message: string): void {
    this.log(message, 'green');
  }

  /**
   * Log a warning message with yellow color
   * @param message - Message to log
   */
  warn(message: string): void {
    this.log(message, 'yellow');
  }

  /**
   * Log an error message with red color
   * @param message - Message to log
   */
  error(message: string): void {
    this.log(message, 'red');
  }

  /**
   * Log a debug message with cyan color
   * @param message - Message to log
   */
  debug(message: string): void {
    this.log(message, 'cyan');
  }

  /**
   * Static method for easy access - log message
   * @param message - Message to log
   * @param color - Color for log display
   */
  static log(message: string, color?: LoggerColor): void {
    UtilLogger.getInstance().log(message, color);
  }

  /**
   * Static method for easy access - info message
   * @param message - Message to log
   */
  static info(message: string): void {
    UtilLogger.getInstance().info(message);
  }

  /**
   * Static method for easy access - success message
   * @param message - Message to log
   */
  static success(message: string): void {
    UtilLogger.getInstance().success(message);
  }

  /**
   * Static method for easy access - warning message
   * @param message - Message to log
   */
  static warn(message: string): void {
    UtilLogger.getInstance().warn(message);
  }

  /**
   * Static method for easy access - error message
   * @param message - Message to log
   */
  static error(message: string): void {
    UtilLogger.getInstance().error(message);
  }

  /**
   * Static method for easy access - debug message
   * @param message - Message to log
   */
  static debug(message: string): void {
    UtilLogger.getInstance().debug(message);
  }
}
